import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useStateRef } from './common';
const { CancelToken } = axios;
export function useCancelRequestHook<T>(props?: {
  defaultLoading?: boolean;
  clear?: () => void;
  destroy?: () => void;
  outControl?: boolean;
}) {
  const {
    defaultLoading,
    clear,
    destroy,
    outControl = false,
  } = Object.assign({ defaultLoading: false, clear: () => {} }, props || {});

  const [loadingRef, loading, setLoading] =
    useStateRef<boolean>(defaultLoading);
  const [error, setError] = useState<boolean>(false);
  const cancelFuncRef = useRef<any>(null);

  function cancelFunc(cancel: any) {
    cancelFuncRef.current = cancel;
  }

  function handleStop() {
    cancelFuncRef.current?.();
    destroy?.();
  }

  function beforeRequest(noLoading = false) {
    if (outControl) return;
    if (!noLoading) {
      setLoading(true);
      clear?.();
    }
    setError(false);
    if (cancelFuncRef.current !== null) {
      cancelFuncRef.current();
      cancelFuncRef.current = null;
      setTimeout(() => {
        !noLoading && setLoading(true);
      }, 1);
    }
  }

  function finallyRequest() {
    if (outControl) return;
    setLoading(false);
  }

  function wrapperRequest(
    requestFunc: (cancelFunc: T) => Promise<T>,
    noLoading = false,
  ) {
    beforeRequest(noLoading);
    return requestFunc(cancelFunc as unknown as any)
      .catch(err => {
        console.log('wrapperRequest :: ', err);
        setError(err.code !== 999);
      })
      .finally(() => {
        finallyRequest();
      });
  }

  function wrapperRequestTile(
    requestFunc: (
      params: any,
      cancelFunc: any,
      refreshTime?: number,
    ) => Promise<T>,
    params: any,
    noLoading = false,
    refreshTime = 0,
  ) {
    beforeRequest(noLoading);
    return requestFunc(params, cancelFunc, refreshTime)
      .catch(err => {
        console.log('wrapperRequest :: ', err);
        setError(err.code !== 999);
      })
      .finally(() => {
        finallyRequest();
      });
  }

  const wrapperCancelRequest = useCallback(
    (
      requestFunc: (params: any) => Promise<T>,
      params: any,
      noLoading = false,
      headers = {},
    ) => {
      beforeRequest(noLoading);
      return requestFunc({
        data: params,
        cancelToken: new CancelToken(cancelFunc),
        headers,
      })
        .catch(err => {
          console.log('wrapperRequest :: ', err);
          const isError = err.code !== 999;
          setError(isError);
          if (isError) throw err;
        })
        .finally(() => {
          finallyRequest();
        });
    },
    [],
  );

  useEffect(() => {
    return () => {
      handleStop();
    };
  }, []);

  return {
    loading,
    wrapperRequest,
    setLoading,
    error,
    wrapperRequestTile,
    handleStop,
    wrapperCancelRequest,
    loadingRef,
  };
}
